import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { GraduationCap, Droplets, Clock, ChevronDown } from "lucide-react";
import { Reveal, Rise } from "./Reveal";
import { SectionBg } from "./SectionBg";
import content from "../content";

const ICONS = { GraduationCap, Droplets };

// Convierte **texto** en negrita dentro de una respuesta del FAQ.
function renderRich(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-white">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
}

export default function MembershipSection() {
  const { membership } = content;
  const reduced = useReducedMotion();
  const [openFaq, setOpenFaq] = useState(null);
  if (!membership || membership.hidden) return null;

  return (
    <section
      id="membresia"
      className="relative w-full bg-ink py-24 md:py-32 overflow-hidden"
      data-testid="membership-section"
    >
      <SectionBg image="/images/membership-bg.webp" tint="ink" />
      <div
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{ background: "radial-gradient(circle at 82% 18%, rgba(33,112,232,0.16), transparent 55%)" }}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-7xl mx-auto px-6">
        {/* Encabezado */}
        <Reveal className="max-w-2xl mb-12 md:mb-16">
          <Rise>
            <span className="inline-flex items-center gap-2 rounded-full border border-electric/30 bg-electric/10 px-4 py-1.5 mb-6">
              <GraduationCap size={16} className="text-electric" />
              <span className="font-poppins text-[11px] tracking-[0.28em] uppercase text-electric font-semibold">
                {membership.eyebrow}
              </span>
            </span>
          </Rise>
          <Rise>
            <h2 className="display text-white text-[clamp(2.5rem,7vw,5rem)] leading-[0.95] mb-5">
              {membership.title}
            </h2>
          </Rise>
          {membership.intro && (
            <Rise>
              <p className="font-poppins text-ceramic/80 text-base md:text-lg leading-relaxed">
                {membership.intro}
              </p>
            </Rise>
          )}
        </Reveal>

        {/* Dos bloques de clases: Membresía + Bautismo */}
        <Reveal className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" amount={0.15}>
          {membership.classes.map((cls) => {
            const Icon = ICONS[cls.icon] || GraduationCap;
            return (
              <Rise key={cls.id}>
                <div
                  className="relative h-full flex flex-col rounded-3xl border border-white/10 bg-white/[0.03] p-7 md:p-9 hover:border-electric/40 transition-colors duration-300"
                  data-testid={`membership-class-${cls.id}`}
                >
                  <div className="flex flex-wrap items-center gap-3 mb-6">
                    <span className="flex items-center justify-center w-11 h-11 rounded-2xl bg-electric/15 border border-electric/30 text-electric">
                      <Icon size={22} strokeWidth={1.8} />
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2">
                      <Clock size={14} className="text-electric" />
                      <span className="font-poppins text-white text-xs font-semibold tracking-wide uppercase">
                        {cls.schedulePill}
                      </span>
                    </span>
                  </div>

                  <h3 className="display text-white text-[clamp(1.75rem,4vw,2.75rem)] leading-none mb-4">
                    {cls.title}
                  </h3>
                  <p className="font-poppins text-ceramic/75 text-sm md:text-base leading-relaxed mb-8">
                    {cls.text}
                  </p>

                  <div className="mt-auto flex flex-wrap gap-3">
                    {cls.dates.map((d) => (
                      <motion.div
                        key={d.day}
                        whileHover={reduced ? {} : { y: -5 }}
                        transition={{ type: "spring", stiffness: 300, damping: 22 }}
                        className="group relative flex-1 min-w-[74px] overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4 text-center hover:border-electric/40 transition-colors duration-300"
                        data-testid={`membership-${cls.id}-date-${d.day}`}
                      >
                        <div
                          className="absolute -right-6 -top-6 w-20 h-20 rounded-full bg-electric/25 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                          aria-hidden="true"
                        />
                        <p className="relative font-poppins text-[10px] tracking-[0.3em] uppercase text-electric mb-1">
                          {d.month}
                        </p>
                        <p className="relative font-anton text-white text-4xl md:text-5xl leading-none">
                          {d.day}
                        </p>
                        <p className="relative font-poppins text-[10px] tracking-[0.2em] uppercase text-white/50 mt-1.5">
                          {d.weekday}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </Rise>
            );
          })}
        </Reveal>

        {/* Preguntas Frecuentes (acordeón desplegable) */}
        {membership.faq?.length > 0 && (
          <Reveal className="mt-16 md:mt-24 max-w-4xl mx-auto" amount={0.1}>
            <Rise>
              <h3 className="display text-white text-[clamp(1.75rem,5vw,3rem)] leading-none mb-8 md:mb-10 text-center">
                {membership.faqTitle || "Preguntas Frecuentes"}
              </h3>
            </Rise>

            <div className="space-y-3" data-testid="membership-faq">
              {membership.faq.map((item, i) => {
                const isOpen = openFaq === i;
                return (
                  <Rise key={i}>
                    <div
                      className={`overflow-hidden rounded-2xl border transition-colors duration-300 ${
                        isOpen ? "border-electric" : "border-white/10"
                      }`}
                      data-testid={`membership-faq-item-${i}`}
                    >
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? null : i)}
                        aria-expanded={isOpen}
                        data-testid={`membership-faq-toggle-${i}`}
                        className={`flex w-full items-center gap-4 px-5 md:px-6 py-4 md:py-5 text-left transition-colors duration-300 ${
                          isOpen ? "bg-electric" : "bg-white/[0.03] hover:bg-white/[0.06]"
                        }`}
                      >
                        <ChevronDown
                          size={22}
                          className={`shrink-0 transition-transform duration-300 ${
                            isOpen ? "rotate-180 text-white" : "text-electric"
                          }`}
                        />
                        <span
                          className={`font-poppins text-sm md:text-base font-semibold tracking-wide uppercase ${
                            isOpen ? "text-white" : "text-white/90"
                          }`}
                        >
                          {item.q}
                        </span>
                      </button>

                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            key="content"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                            className="bg-white/[0.02]"
                          >
                            <div className="px-5 md:px-6 py-5 md:py-6 border-t border-white/10">
                              <p className="font-poppins text-ceramic/80 text-sm md:text-base leading-relaxed">
                                {renderRich(item.a)}
                              </p>
                              {item.bullets && (
                                <ul className="mt-4 space-y-2.5">
                                  {item.bullets.map((b, bi) => (
                                    <li
                                      key={bi}
                                      className="flex gap-3 font-poppins text-ceramic/80 text-sm md:text-base leading-relaxed"
                                    >
                                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-electric" />
                                      <span>{renderRich(b)}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </Rise>
                );
              })}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
